import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Square, Play, RotateCcw, Check, X, AlertCircle } from 'lucide-react';

const VideoRecorder = ({ onRecordingComplete, onCancel, postureTitle }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: true
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError('Unable to access camera. Please check permissions.');
      console.error('Camera access error:', err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCountdown = () => {
    setCountdown(3);
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          startRecording();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    try {
      chunksRef.current = [];
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp9'
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        stopCamera();
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      setError('Recording failed. Please try again.');
      console.error('Recording error:', err);
    }
  }, [stopCamera]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const retakeVideo = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsPlaying(false);
    startCamera();
  };

  const confirmRecording = () => {
    if (recordedBlob) {
      onRecordingComplete(recordedBlob);
    }
  };

  const handleCancel = () => {
    stopCamera();
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    onCancel();
  };

  // Initialize camera on mount
  React.useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [startCamera, stopCamera, recordedUrl]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-dark-900 rounded-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="p-6 border-b border-dark-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-dark-100">
              Record: {postureTitle}
            </h2>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-dark-700 rounded-lg transition-colors duration-200"
            >
              <X className="w-6 h-6 text-dark-400" />
            </button>
          </div>
        </div>

        {/* Video Area */}
        <div className="flex-1 p-6">
          <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
            {/* Live Camera Feed or Recorded Video */}
            <video
              ref={videoRef}
              className="w-full h-full object-cover scale-x-[-1]"
              autoPlay
              muted={!recordedUrl}
              playsInline
              src={recordedUrl}
            />

            {/* Recording Indicator */}
            <AnimatePresence>
              {isRecording && (
                <motion.div
                  className="absolute top-4 left-4 flex items-center space-x-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="w-3 h-3 bg-red-500 rounded-full"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <span className="text-white text-sm font-medium">Recording</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Countdown Overlay */}
            <AnimatePresence>
              {countdown && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="text-8xl font-bold text-white"
                    key={countdown}
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {countdown}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
                <div className="text-center text-white p-6">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                  <p className="text-lg mb-4">{error}</p>
                  <button
                    onClick={startCamera}
                    className="btn-primary"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 border-t border-dark-700">
          {!recordedBlob ? (
            // Recording Controls
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={handleCancel}
                className="btn-secondary"
              >
                Cancel
              </button>
              
              {!isRecording ? (
                <motion.button
                  onClick={startCountdown}
                  className="btn-primary flex items-center space-x-2 px-8 py-4"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!!error}
                >
                  <Camera className="w-5 h-5" />
                  <span>Start Recording</span>
                </motion.button>
              ) : (
                <motion.button
                  onClick={stopRecording}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Square className="w-5 h-5" />
                  <span>Stop Recording</span>
                </motion.button>
              )}
            </div>
          ) : (
            // Review Controls
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={retakeVideo}
                className="btn-secondary flex items-center space-x-2"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Retake</span>
              </button>
              
              <button
                onClick={() => {
                  if (videoRef.current) {
                    if (isPlaying) {
                      videoRef.current.pause();
                    } else {
                      videoRef.current.play();
                    }
                    setIsPlaying(!isPlaying);
                  }
                }}
                className="btn-secondary flex items-center space-x-2"
              >
                <Play className="w-5 h-5" />
                <span>Review</span>
              </button>
              
              <button
                onClick={confirmRecording}
                className="btn-primary flex items-center space-x-2"
              >
                <Check className="w-5 h-5" />
                <span>Use This Recording</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VideoRecorder;
